import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Eye, Trash2, Loader2 } from 'lucide-react';
import { JobStatusBadge } from './JobStatusIndicator';
import type { PredictionErrorEvaluationJob } from '@/lib/types/ds/prediction-error-evaluation';

interface JobHistoryTableProps {
  jobs: PredictionErrorEvaluationJob[];
  onSelect: (job: PredictionErrorEvaluationJob) => void;
  onDelete?: (job: PredictionErrorEvaluationJob) => void;
  deletingJobs?: Set<string>;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function JobHistoryTable({
  jobs,
  onSelect,
  onDelete,
  deletingJobs = new Set(),
}: JobHistoryTableProps) {
  if (jobs.length === 0) {
    return <p className="text-sm text-muted-foreground">ジョブ履歴がありません</p>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ステータス</TableHead>
            <TableHead>アカウント</TableHead>
            <TableHead>基準日</TableHead>
            <TableHead>組合せ数</TableHead>
            <TableHead>実行者</TableHead>
            <TableHead>作成日時</TableHead>
            <TableHead className="w-24"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => (
            <TableRow
              key={job.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSelect(job)}
            >
              <TableCell>
                <JobStatusBadge status={job.job_status} />
              </TableCell>
              <TableCell className="font-mono text-sm">{job.account_code}</TableCell>
              <TableCell className="text-sm">{(job.target_dates ?? []).join(', ')}</TableCell>
              <TableCell className="text-sm">{job.total_combinations ?? '-'}</TableCell>
              <TableCell className="text-sm">{job.executed_by}</TableCell>
              <TableCell className="text-sm">{formatDate(job.created_at)}</TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => onSelect(job)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  {onDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" disabled={deletingJobs.has(job.id)}>
                          {deletingJobs.has(job.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>ジョブの削除確認</AlertDialogTitle>
                          <AlertDialogDescription>
                            このジョブを削除してもよろしいですか？
                            <br />
                            アカウント: {job.account_code} / 基準日:{' '}
                            {(job.target_dates ?? []).join(', ')}
                            <br />
                            <strong>
                              S3上のファイルも含め、この操作は取り消すことができません。
                            </strong>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>キャンセル</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(job)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            削除する
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
